import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { ApprovalTierValue, StructuredBrief } from '@socialsoup/shared';

export interface ChannelDetailLatestResult {
  id: string;
  resultsData: Record<string, unknown>;
  lastDataRefresh: string | null;
  updatedAt: string;
  version: number;
}

/**
 * R5.9 — `clienthubId` on this interface is DEPRECATED.
 *
 * Post-R5 `Campaign.clienthubId` no longer exists; the per-group ClientHub
 * linkage is on `CampaignGroup.clienthubId` (see `campaignGroup` below).
 * The legacy field is left typed as `null` to keep the wire shape stable
 * for any pre-R5 fetched cache; new readers should use
 * `ChannelDetail.campaignGroup.clienthubId` instead.
 */
export interface ChannelDetailCampaign {
  id: string;
  name: string;
  /** @deprecated read `ChannelDetail.campaignGroup.clienthubId` instead. */
  clienthubId?: null;
}

/** R5.6 — group surface for "is this channel's group ClientHub-linked?". */
export interface ChannelDetailCampaignGroup {
  id: string;
  clienthubId: string | null;
  /** PR-D: resolved group tier (NULL → 'FULL' server-side) — gates the Spot check tab. */
  approvalTier: ApprovalTierValue;
  /** Spot-check opt-in (2026-07-13): the Spot check tab additionally requires this. */
  needsSpotCheck: boolean;
}

export interface ChannelDetail {
  id: string;
  clienthubChannelId: string;
  name: string;
  type: string;
  status: string | null;
  objective: string | null;
  skip: boolean;
  actionsRequiredCount: number;
  clienthubSyncedAt: string | null;
  clienthubLaterCampaignId: string | null;
  laterCampaignIds: number[];
  /** Phase 9 (TODO-117) — commercial + content-brief contract. */
  brief: string;
  /** Per-creator minimum deliverables ("Minimum deliverables per creator"). */
  deliverableCount: number;
  /** PR-B (roadmap 1.5): creators to select — drives the 130% shortlist indicator. */
  targetCreatorCount: number | null;
  /** PR-C (roadmap 1.4): brief approval state. */
  briefStatus: 'DRAFT' | 'WITH_CLIENT' | 'APPROVED';
  briefFeedbackRounds: number;
  briefApprovedAt: string | null;
  briefClientFeedback: string | null;
  briefClientFeedbackAt: string | null;
  /** Admin-only commercial figure. */
  budget: number | null;
  /** Admin-only commercial figure. */
  paymentPerDeliverable: number | null;
  /** Phase 9 (§5.1) — required-hashtag compliance list for this channel's brief. */
  requiredHashtags: string[];
  /** Phase 9 (TODO-117) — single Posts-Tracker match key. */
  trackingHashtag: string | null;
  /** Phase 9 (TODO-117) — creator-facing copy-paste hashtag suggestions. */
  recommendedHashtags: string[];
  /**
   * B3 (goal-alignment) — structured brief from Campaign.guidelines, returned by
   * getChannelDetail. Optional/union to allow legacy free-text-only channels;
   * use isStructuredBrief() from @socialsoup/shared to narrow.
   */
  guidelines?: StructuredBrief | Record<string, unknown> | null;
  /** A1 — hides all sample posts from client/magic-link shortlist views. */
  hideSamplePosts: boolean;
  /** A1 — hides all audience data from client/magic-link shortlist views. */
  hideAllAudienceData: boolean;
  /** Phase 3 — live channel default inherited by creator rights previews. */
  defaultContentRights?: string | null;
  /**
   * Phase 4 (concept-approval-flow) — gates the Concepts tab (consistent
   * with `campaignGroup.needsSpotCheck` gating the Spot check tab) and seeds
   * `ConceptsEnabledToggle`'s initial value. Defaults `false` on every
   * pre-existing channel.
   */
  conceptsEnabled: boolean;
  postCount: number;
  resultsCount: number;
  latestResult: ChannelDetailLatestResult | null;
  campaign: ChannelDetailCampaign;
  /** R5.6: navigate here for "is this channel's group ClientHub-linked?". */
  campaignGroup: ChannelDetailCampaignGroup;
}

export const channelDetailKeys = {
  all: ['channels'] as const,
  detail: (campaignId: string, channelId: string) =>
    ['campaigns', campaignId, 'channels', channelId] as const,
};

export function useChannelDetail(campaignId: string | undefined, channelId: string | undefined) {
  return useQuery({
    queryKey: channelDetailKeys.detail(campaignId ?? '', channelId ?? ''),
    queryFn: () =>
      api
        .get<ChannelDetail>(`/api/v1/campaigns/${campaignId}/channels/${channelId}`)
        .then((res) => res.data),
    enabled: !!campaignId && !!channelId,
  });
}

// ─── useChannelById (R4) ──────────────────────────────────────────────────────
// Lightweight lookup by channel id only — used by LegacyCampaignRedirect to
// resolve the parent campaignGroupId without needing the group id up front.

export interface ChannelByIdResult {
  id: string;
  campaignGroupId: string;
  name: string;
  status: string;
  type: string | null;
}

export const channelByIdKeys = {
  detail: (id: string) => ['admin', 'channels', 'by-id', id] as const,
};

export function useChannelById(channelId: string | undefined) {
  return useQuery({
    queryKey: channelByIdKeys.detail(channelId ?? ''),
    queryFn: () =>
      api.get<ChannelByIdResult>(`/api/v1/channels/${channelId}`).then((res) => res.data),
    enabled: !!channelId,
    staleTime: 30_000,
  });
}
